/**
 * Talaffuz qadami: mikrofondan yozuv (recorder.ts, WAV) → API `/v1/speech/check` (Gemini aytilgan so'zni yozadi, server solishtiradi).
 * Mikrofon ruxsati yo'q / server ishlamasa — `listen` (eshitib tanlash), ovoz ham bo'lmasa — `recUz`.
 */
import { useEffect, useRef, useState } from 'react';
import { t } from '../../../i18n';
import { ApiError } from '../../../lib/api';
import { canRecord, micHeld, startRecording, type Recorder } from '../../../lib/recorder';
import { canSpeak, hush, MIC_OK, setMicOk, speak } from '../../../lib/speech';
import { sync } from '../../../lib/sync';
import { Ic } from '../../../ui/Icon';
import { replaceStep, useLesson, type Step } from '../engine';
import { answer, Frame, Prompt } from './common';

const NOTE = 'Mikrofon ishlamadi, shuning uchun talaffuz eshitib tanlash orqali tekshiriladi.';
const MAX_MS = 4000;
/** Darsdagi birinchi yozuvdan oldin: Telegram ruxsat oynasini oldindan tushuntiramiz (keyin dars davomida so'ramaydi). */
const ASK = 'Bosing va gapiring. Telegram soʻrasa, mikrofonga ruxsat bering';
/** Shundan past cho'qqi — jimlik (serverga yuborilmaydi). */
const SILENCE = 0.02;
/** Ketma-ket server xatolari (2 ta bo'lsa — sessiya oxirigacha eshitib tanlash). */
let fails = 0;

/** Mikrofon/server ishlamasa — eshitib tanlash (ovoz ham bo'lmasa — variantlar bilan). */
function fallback() {
  if (!canSpeak()) { replaceStep({ t: 'recUz' }); return; }
  const st = useLesson.getState();
  replaceStep({ t: 'listen', note: st.L?.noteShown ? '' : NOTE });
  st.set({ noteShown: true });
}

type Phase = 'idle' | 'rec' | 'check';

export function SpeakStep({ s }: { s: Step }) {
  const w = s.w!;
  const broken = !canRecord() || MIC_OK === false;
  const [phase, setPhase] = useState<Phase>('idle');
  const [status, setStatus] = useState(() => (micHeld() ? 'Bosing va gapiring' : ASK));
  const rec = useRef<Recorder | null>(null);
  const tm = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const alive = useRef(true);

  useEffect(() => { if (broken) fallback(); }, [broken]);
  useEffect(() => { alive.current = true; return () => { alive.current = false; rec.current?.cancel(); clearTimeout(tm.current); }; }, []);
  if (broken) return null;

  const check = async () => {
    clearTimeout(tm.current);
    const r = rec.current; rec.current = null; if (!r) return;
    const a = await r.stop();
    if (!alive.current) return;
    if (a.peak < SILENCE || a.ms < 400) { setPhase('idle'); setStatus('Eshitilmadi. Yana bosib, aniqroq ayting'); return; }
    setPhase('check'); setStatus('Tekshirilmoqda…');
    try {
      const res = await sync.speech(w.id, a.b64);
      if (!alive.current) return;
      fails = 0; setMicOk(true); setPhase('idle'); setStatus('Bosing va gapiring');
      const said = res.heard[0] ?? '';
      if (!res.ok && !said) { setStatus('Eshitilmadi. Yana bosib, aniqroq ayting'); return; }
      const full = t(`Toʻgʻrisi: ${w.w} ${w.ipa}. Bu soʻz shu bosqich oxirida yana chiqadi.`), k = full.indexOf(w.w);
      answer(w, res.ok, res.ok ? {} : {
        head: t('Yana urinib koʻring'),
        ans: <span style={{ fontSize: 18, fontWeight: 700 }}>{t(`Siz aytdingiz: “${said}”`)}</span>,
        msg: k >= 0 ? <>{full.slice(0, k)}<b>{w.w}</b>{full.slice(k + w.w.length)}</> : full,
      });
    } catch (e) {
      if (!alive.current) return;
      setPhase('idle');
      const hard = e instanceof ApiError && (e.status === 503 || e.status === 429);
      if (hard || ++fails >= 2) { setMicOk(false); fallback(); return; }
      setStatus('Tekshirib boʻlmadi. Yana bosib koʻring');
    }
  };

  const onMic = async () => {
    if (phase === 'check') return;
    if (phase === 'rec') { void check(); return; }
    hush();
    try {
      rec.current = await startRecording();
    } catch (e) {
      console.warn('[mic]', e);
      setMicOk(false); fallback(); return;
    }
    if (!alive.current) { rec.current?.cancel(); return; }
    setPhase('rec'); setStatus('Gapiring… tugatgach yana bosing');
    tm.current = setTimeout(() => { void check(); }, MAX_MS);
  };

  return (
    <Frame foot={<button className="btn soft" id="skip" onClick={() => { rec.current?.cancel(); rec.current = null; replaceStep({ t: 'listen' }); }}>{t('Hozir gapira olmayman')}</button>}>
      <Prompt retry={s.retry} text="Soʻzni ovoz chiqarib ayting" />
      <div className="qcard" style={{ alignItems: 'center', textAlign: 'center', padding: '16px 16px 22px' }}>
        <b style={{ fontSize: 34, lineHeight: '42px' }}>{w.w}</b>
        <span className="t13" style={{ fontSize: 15 }}>{w.ipa} · {w.uz}</span>
        <button className="btn soft" id="au" disabled={phase !== 'idle'} style={{ height: 44, width: 'auto', padding: '0 16px', marginTop: 10, gap: 8 }} onClick={() => speak(w.w)}><Ic n="vol" style={{ width: 18, height: 18 }} />{t('Namunani tinglash')}</button>
      </div>
      <div className="col g10" style={{ alignItems: 'center' }}>
        <button className={`mic${phase === 'rec' ? ' on' : ''}`} id="mic" aria-label={t('Gapirish')} disabled={phase === 'check'} onClick={() => { void onMic(); }}><Ic n="mic" style={{ width: 34, height: 34 }} /></button>
        <span className="t13" id="st">{t(status)}</span>
      </div>
    </Frame>
  );
}
